import * as React from 'react';
import { IEvent, ILocalEvent } from '@app-types/websocket/event.types';
import { IExternalCalenderEvent } from '@app-types/api/externalCalendar.types';
import { toIsoDate } from '@helpers/DateHelper';

export interface IMonthEvent {
  e: IEvent | ILocalEvent | IExternalCalenderEvent;
  event: IEvent | null;
  importedEvent: ILocalEvent | null;
  externalEvent: IExternalCalenderEvent | null;
}

interface ITimeSpan {
  startTime: number;
  endTime: number;
}

// The stretch of an event inside one week row, drawn as one bar from startCol to endCol
export interface IEventSegment {
  item: IMonthEvent;
  lane: number;
  startCol: number;
  endCol: number;
  continuesBefore: boolean; // the event started before the row's first day
  continuesAfter: boolean; // the event goes on after the row's last day
}

export interface IDayEventLayout {
  // Lanes from the top down to the last bar this day starts. A null lane stays empty: it is a gap,
  // or a bar started by an earlier day in the row runs through it.
  lanes: Array<IEventSegment | null>;
  hasEvents: boolean;
  // Days crossed by the same multi-day bars share a group, and their events have to start at the same height.
  // -1 when no bar longer than a day crosses the day.
  alignGroup: number;
}

// The end time is exclusive, so an event ending at midnight stays off the next day
const lastInstant = (e: ITimeSpan): number => (e.endTime > e.startTime ? e.endTime - 1 : e.startTime);

const overlaps = (e: ITimeSpan, start: number, end: number): boolean => e.startTime <= end && lastInstant(e) >= start;

// Picks the events that touch start..end. The external lists are per day and list an event on every day it covers:
// the same object when just fetched, but separate copies after the store is restored from storage, so they are
// matched by value. Occurrences of a repeating event share an id but not their times.
export const collectEvents = (start: number, end: number, events: IEvent[], importedEvents: ILocalEvent[],
  externalLists: Array<IExternalCalenderEvent[] | undefined>): IMonthEvent[] => {
  const items: IMonthEvent[] = [];
  const seen = new Set<string>();
  for (const list of externalLists) {
    if (list === undefined) { continue; }
    for (const externalEvent of list) {
      const key = externalEvent.id + '|' + externalEvent.startTime + '|' + externalEvent.endTime + '|' + externalEvent.tagged
        + '|' + externalEvent.title;
      if (seen.has(key)) { continue; }
      seen.add(key);
      if (overlaps(externalEvent, start, end)) {
        items.push({ e: externalEvent, event: null, importedEvent: null, externalEvent });
      }
    }
  }
  for (const event of events) {
    if (overlaps(event, start, end)) {
      items.push({ e: event, event, importedEvent: null, externalEvent: null });
    }
  }
  for (const importedEvent of importedEvents) {
    if (overlaps(importedEvent, start, end)) {
      items.push({ e: importedEvent, event: null, importedEvent, externalEvent: null });
    }
  }
  return items;
};

const compareSegments = (a: IEventSegment, b: IEventSegment): number => {
  if (a.startCol !== b.startCol) { return a.startCol - b.startCol; }
  // events that started before the row go first, the one ending soonest on top
  if (a.continuesBefore !== b.continuesBefore) { return a.continuesBefore ? -1 : 1; }
  if (a.continuesBefore && a.item.e.endTime !== b.item.e.endTime) { return a.item.e.endTime - b.item.e.endTime; }
  if (a.item.e.startTime !== b.item.e.startTime) { return a.item.e.startTime - b.item.e.startTime; }
  // same start: the longer one on top
  return b.item.e.endTime - a.item.e.endTime;
};

// Lays out the events of one week row (consecutive days, at most 7). Each event takes the highest lane
// that is free on all of its days, so a bar keeps its lane and the lanes above it can have gaps.
export const layoutRowEvents = (days: ITimeSpan[], items: IMonthEvent[]): IDayEventLayout[] => {
  const lastCol = days.length - 1;
  const rowStart = days[0].startTime;
  const rowEnd = days[lastCol].endTime;
  const colOf = (time: number): number => {
    let col = 0;
    while (col < lastCol && time > days[col].endTime) { col++; }
    return col;
  };

  const segments: IEventSegment[] = [];
  for (const item of items) {
    const end = lastInstant(item.e);
    if (item.e.startTime > rowEnd || end < rowStart) { continue; }
    const continuesBefore = item.e.startTime < rowStart;
    const continuesAfter = end > rowEnd;
    segments.push({
      item,
      lane: 0,
      startCol: continuesBefore ? 0 : colOf(item.e.startTime),
      endCol: continuesAfter ? lastCol : colOf(end),
      continuesBefore,
      continuesAfter,
    });
  }

  // sort indexes, so equal events keep their input order also where Array.sort is not stable (older WebViews)
  const order: number[] = [];
  for (let i = 0; i < segments.length; i++) { order.push(i); }
  order.sort((a, b) => compareSegments(segments[a], segments[b]) || a - b);

  const laneEnds: number[] = []; // the last column taken in each lane
  const depth: number[] = []; // per day: lanes needed down to the last bar the day starts
  const layouts: IDayEventLayout[] = [];
  for (let col = 0; col <= lastCol; col++) {
    depth.push(0);
    layouts.push({ lanes: [], hasEvents: false, alignGroup: -1 });
  }

  let group = -1;
  let groupEnd = -1;
  for (const index of order) {
    const segment = segments[index];
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane] >= segment.startCol) { lane++; }
    segment.lane = lane;
    laneEnds[lane] = segment.endCol;
    depth[segment.startCol] = Math.max(depth[segment.startCol], lane + 1);

    for (let col = segment.startCol; col <= segment.endCol; col++) { layouts[col].hasEvents = true; }

    if (segment.endCol > segment.startCol) { // bars come in start order, so overlapping spans merge into one group
      if (segment.startCol > groupEnd) { group++; }
      groupEnd = Math.max(groupEnd, segment.endCol);
      for (let col = segment.startCol; col <= segment.endCol; col++) { layouts[col].alignGroup = group; }
    }
  }

  for (let col = 0; col <= lastCol; col++) {
    for (let lane = 0; lane < depth[col]; lane++) { layouts[col].lanes.push(null); }
  }
  for (const segment of segments) {
    layouts[segment.startCol].lanes[segment.lane] = segment;
  }
  return layouts;
};

// Lays out a month row by row; padding is the number of empty cells before the 1st. Returns one layout per
// day of the month, with align groups numbered across the whole month.
export const layoutMonthEvents = (days: ITimeSpan[], padding: number, events: IEvent[], importedEvents: ILocalEvent[],
  externalEventsPerDay: { [day: string]: IExternalCalenderEvent[] }): IDayEventLayout[] => {
  // lists stored by older versions of the app leave out a multi-day event's last day, so also look at the day before
  // the month
  const externalLists: Array<IExternalCalenderEvent[] | undefined> = [externalEventsPerDay[toIsoDate(new Date(days[0].startTime - 1))]];
  for (const day of days) {
    externalLists.push(externalEventsPerDay[toIsoDate(new Date((day.startTime + day.endTime + 1) / 2))]);
  }
  const items = collectEvents(days[0].startTime, days[days.length - 1].endTime, events, importedEvents, externalLists);

  const layouts: IDayEventLayout[] = [];
  let groupBase = 0;
  let rowStart = 0;
  let rowLength = 7 - padding;
  while (rowStart < days.length) {
    let groups = 0;
    for (const layout of layoutRowEvents(days.slice(rowStart, rowStart + rowLength), items)) {
      if (layout.alignGroup !== -1) {
        groups = Math.max(groups, layout.alignGroup + 1);
        layout.alignGroup += groupBase;
      }
      layouts.push(layout);
    }
    groupBase += groups;
    rowStart += rowLength;
    rowLength = 7;
  }
  return layouts;
};

// Per day in an align group: the days whose header blocks it copies, invisibly, to start its events as low as
// they do. One day per distinct block, and none with the same block as its own: the same text is as tall.
// blockKeys has a key for each day's block, the same key for the same content, or null when the day has none.
export const blocksToCopy = (layouts: IDayEventLayout[], blockKeys: Array<string | null>): number[][] => {
  const members: { [group: string]: number[] } = {};
  layouts.forEach((layout, day) => {
    if (layout.alignGroup !== -1) { (members[layout.alignGroup] = members[layout.alignGroup] || []).push(day); }
  });
  const result: number[][] = layouts.map(() => []);
  for (const group of Object.keys(members)) {
    for (const day of members[group]) {
      const copied: string[] = [];
      for (const other of members[group]) {
        const key = blockKeys[other];
        if (other === day || key === null || key === blockKeys[day] || copied.indexOf(key) !== -1) { continue; }
        copied.push(key);
        result[day].push(other);
      }
    }
  }
  return result;
};

// Edit mode: the days that keep the space of their old-schedule bars, invisibly, because another day crossed by the
// same multi-day bars shows its old-schedule bars. The real bar keeping its size, instead of a spacer next to it,
// makes every day of the group grow and shrink with the same transition, so the lanes stay in line as it animates.
export const reserveOldBarDays = (layouts: IDayEventLayout[], isHighlight: boolean[]): boolean[] => {
  const shown: { [group: number]: boolean } = {};
  layouts.forEach((layout, day) => {
    if (layout.alignGroup !== -1 && isHighlight[day]) { shown[layout.alignGroup] = true; }
  });
  return layouts.map((layout, day) => layout.alignGroup !== -1 && !isHighlight[day] && shown[layout.alignGroup] === true);
};

// Shapes an event chip: rounded where the event starts or ends, square where it goes on in another row,
// and wide enough to run over the following days of its row
export const segmentStyle = (segment: IEventSegment): React.CSSProperties => {
  const spanDays = segment.endCol - segment.startCol + 1;
  // 1px off the cell edge where the event starts or ends, so the grid line and a focused day's border show next to it
  const gapLeft = segment.continuesBefore ? 0 : 1;
  const gapRight = segment.continuesAfter ? 0 : 1;
  return {
    // from the day's left edge instead of centered, running on over the next days
    alignSelf: 'flex-start',
    // over the next days' cells and over every cell's focus or selection box, one-day chips and bars alike
    position: 'relative',
    zIndex: 1,
    marginLeft: gapLeft + 'px',
    width: 'calc(' + (spanDays * 100) + '% - ' + (gapLeft + gapRight) + 'px)',
    borderRadius: (segment.continuesBefore ? '0px ' : '2px ') + (segment.continuesAfter ? '0px 0px ' : '2px 2px ')
      + (segment.continuesBefore ? '0px' : '2px'),
  };
};

// Lanes are a chip plus its 1px bottom margin apart: 18px for the normal 17px chip, down to 9px for an 8px one
export const NORMAL_LANE_PITCH = 18;
export const MIN_LANE_PITCH = 9;

// The tallest lane pitch that fits `lanes` lanes into `space` px (the last chip needs no margin under it)
export const fitLanePitch = (space: number, lanes: number): number => {
  if (lanes <= 0) { return NORMAL_LANE_PITCH; }
  return Math.max(MIN_LANE_PITCH, Math.min(NORMAL_LANE_PITCH, Math.floor((space + 1) / lanes)));
};

const denseChipStyles: { [pitch: number]: React.CSSProperties } = {};

// The chip style for a week whose lanes must be `pitch` px apart to show all its events: less padding first,
// then a smaller font. Undefined for the normal pitch. The same object per pitch, so re-renders see no change.
export const denseChipStyle = (pitch: number | undefined): React.CSSProperties | undefined => {
  if (pitch === undefined || pitch >= NORMAL_LANE_PITCH) { return undefined; }
  if (denseChipStyles[pitch] === undefined) {
    const height = pitch - 1;
    const lineHeight = Math.min(11, height);
    const paddingTop = Math.floor((height - lineHeight) / 2);
    denseChipStyles[pitch] = {
      height: height + 'px',
      lineHeight: lineHeight + 'px',
      fontSize: lineHeight + 'px',
      padding: paddingTop + 'px 2px ' + (height - lineHeight - paddingTop) + 'px',
    };
  }
  return denseChipStyles[pitch];
};



// WEBPACK FOOTER //
// ./src/components/calendar2/monthView/monthEventLayout.ts